/**
 * FP3-035B — Print view for a completed articulation screener.
 *
 * Reuses the shared ArticulationScreenerResultPanel as the body, so
 * the print view, the dialog viewer, and the display view stay in
 * lockstep. The page header carries student name + DOB/age,
 * assessment date, screener version, and the provider blocks
 * (notes / clinical summary / recommendations) which already live
 * inside the result panel.
 *
 * Auth: same protection as the other /print/students/... routes —
 * the page calls the existing authenticated APIs; if the provider
 * lacks visibility, PrintAccessGate renders an access-denied state.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import {
  PrintShell,
  PrintAccessGate,
  classifyError,
} from "@/components/print/PrintShell";
import ArticulationScreenerResultPanel from "@/components/student/ArticulationScreenerResultPanel";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export default function PrintAssessmentRun() {
  const { studentId, runId } = useParams();
  const [student, setStudent] = useState(null);
  const [run, setRun] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.getStudent(studentId),
      api.getAssessmentRun(studentId, runId),
    ])
      .then(([sResp, rResp]) => {
        if (cancelled) return;
        setStudent(sResp.data);
        setRun(rResp.data);
        setPhase("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err.message);
        setPhase(classifyError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [studentId, runId]);

  const name = student ? `${student.first_name} ${student.last_name}` : "Student";
  const dob = student?.date_of_birth;
  const ageStamp = run?.student_age_at_assessment?.label;
  const version =
    run?.screener_template_id === "ages_3_5"
      ? "Articulation Screener — Ages 3-5"
      : run?.screener_template_id === "ages_5_8"
      ? "Articulation Screener — Ages 5-8"
      : run?.title;

  return (
    <PrintShell
      title="Articulation Screener Result"
      subtitle={`${name}${student?.readable_id ? ` · ${student.readable_id}` : ""}`}
      testId="print-assessment-run"
    >
      <PrintAccessGate phase={phase} error={error}>
        {student && run && (
          <>
            {/* Header block — student + assessment metadata */}
            <div className="fp-print-section">
              <div className="fp-print-grid-2">
                <div>
                  <div className="fp-print-section-label">Student</div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>{name}</div>
                  <div className="fp-print-kv-row">
                    <span className="fp-print-kv-key">Readable ID</span>
                    <span className="fp-print-kv-val">{student.readable_id || "—"}</span>
                  </div>
                  <div className="fp-print-kv-row">
                    <span className="fp-print-kv-key">Date of birth</span>
                    <span className="fp-print-kv-val">{fmtDate(dob)}</span>
                  </div>
                  <div className="fp-print-kv-row">
                    <span className="fp-print-kv-key">Age at assessment</span>
                    <span className="fp-print-kv-val">{ageStamp || "—"}</span>
                  </div>
                  {/* FP3-080 — Sex (source of truth for norm tables)
                      replaces the removed Speech Norm Category row. */}
                  {student.sex && (
                    <div className="fp-print-kv-row">
                      <span className="fp-print-kv-key">Sex</span>
                      <span className="fp-print-kv-val">
                        {student.sex === "female"
                          ? "Female"
                          : student.sex === "male"
                          ? "Male"
                          : student.sex === "not_specified"
                          ? "Not specified"
                          : student.sex.charAt(0).toUpperCase() + student.sex.slice(1)}
                      </span>
                    </div>
                  )}
                  <div className="fp-print-kv-row">
                    <span className="fp-print-kv-key">Grade · School</span>
                    <span className="fp-print-kv-val">
                      {[
                        student.grade ? `Grade ${student.grade}` : "",
                        student.school_name,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="fp-print-section-label">Assessment</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{version}</div>
                  <div className="fp-print-kv-row">
                    <span className="fp-print-kv-key">Date administered</span>
                    <span className="fp-print-kv-val">{fmtDate(run.date_administered)}</span>
                  </div>
                  <div className="fp-print-kv-row">
                    <span className="fp-print-kv-key">Skill area</span>
                    <span className="fp-print-kv-val">{run.skill_area || "—"}</span>
                  </div>
                  <div className="fp-print-kv-row">
                    <span className="fp-print-kv-key">Items</span>
                    <span className="fp-print-kv-val">
                      {run.summary?.scored_count ?? 0} scored / {run.summary?.total_count ?? 0} total
                    </span>
                  </div>
                  <div className="fp-print-kv-row">
                    <span className="fp-print-kv-key">Overall accuracy</span>
                    <span className="fp-print-kv-val">
                      {run.summary?.percent_accuracy == null
                        ? "—"
                        : `${run.summary.percent_accuracy}%`}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Body — the same rich result panel that the in-app
                viewer renders, so the print and screen views never
                drift in math or layout. */}
            <div data-testid="print-assessment-run-body" style={{ marginTop: 12 }}>
              <ArticulationScreenerResultPanel
                run={run}
                student={student}
                hideInterpretationActions
              />
            </div>
          </>
        )}
      </PrintAccessGate>
    </PrintShell>
  );
}
